import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HeartHandshake, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PRESETS = [3, 5, 10];

export default function DonateDialog({ open, onOpenChange, defaultName = "", defaultEmail = "" }) {
  const [amount, setAmount] = useState(5);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);

  const pounds = custom !== "" ? Number(custom) : amount;

  const donate = async () => {
    const value = Number(pounds);
    if (!Number.isFinite(value) || value < 1) { toast.error("Please enter an amount of £1 or more"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        amount_pence: Math.round(value * 100),
        donor_name: name || undefined,
        donor_email: email || undefined,
        origin: window.location.origin,
      });
      window.location.assign(data.checkout_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start checkout — please try again shortly.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-md" data-testid="donate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <HeartHandshake className="w-5 h-5 text-primary" /> Support CodexStudio
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-1">
          <p className="text-sm text-muted-foreground">
            CodexStudio is built by one writer, for writers. If it's helping your story, a small donation
            keeps the lights on and the features coming. Thank you.
          </p>

          <div className="grid grid-cols-3 gap-2">
            {PRESETS.map((n) => (
              <Button
                key={n}
                type="button"
                variant={custom === "" && amount === n ? "default" : "outline"}
                onClick={() => { setAmount(n); setCustom(""); }}
                data-testid={`donate-preset-${n}`}
                className="h-12 text-base font-semibold"
              >
                £{n}
              </Button>
            ))}
          </div>

          <div className="space-y-1.5">
            <Label>Or a custom amount (£)</Label>
            <Input
              type="number" min="1" step="1" inputMode="numeric"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="e.g. 20"
              data-testid="donate-custom-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Name <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="donate-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Email <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="For a receipt" data-testid="donate-email-input" />
            </div>
          </div>

          <Button onClick={donate} disabled={busy} className="w-full h-12 text-base gap-2" data-testid="donate-submit-button">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening secure checkout…</> : `Donate £${Number.isFinite(Number(pounds)) && pounds ? pounds : 0}`}
          </Button>
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
            <Lock className="w-3 h-3" /> Secure payment handled by Square. We never see your card details.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
